import type { Device } from '@mogzo/game-ui';
import { type LaunchParams, launchParamsSchema } from '@mogzo/shared';
import { useCallback, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router';
import type { PlayableBuild } from '../../lib/communities';
import { PhonePlayer } from './PhonePlayer';

const PHONE_QUERY = '(max-width: 767px), (max-height: 500px) and (pointer: coarse)';
/** Tablets, touch screens that aren't phones, and narrow windows get the Tablet look. */
const TABLET_QUERY = '(max-width: 1024px), (pointer: coarse)';
/** A touch screen: phones and tablets, whatever their size. */
const TOUCH_QUERY = '(pointer: coarse)';

/** Phones, landscape phones included. */
export function isPhone(): boolean {
  return window.matchMedia?.(PHONE_QUERY).matches ?? false;
}

/**
 * Phones and tablets launch games full screen, since they're played by touch; computers open
 * the play page.
 */
export function launchesFullScreen(): boolean {
  return isPhone() || (window.matchMedia?.(TOUCH_QUERY).matches ?? false);
}

/** The screen size MOGZO's panels are drawn for (M19). */
export function screenSize(): Device {
  if (isPhone()) return 'phone';
  return window.matchMedia?.(TABLET_QUERY).matches ? 'tablet' : 'computer';
}

function watchSize(onChange: () => void): () => void {
  const queries = [PHONE_QUERY, TABLET_QUERY].map((query) => window.matchMedia?.(query));
  for (const query of queries) query?.addEventListener?.('change', onChange);
  return () => {
    for (const query of queries) query?.removeEventListener?.('change', onChange);
  };
}

/** screenSize that follows the window, so a resized window (or closed dev tools) gets the right panels. */
export function useScreenSize(): Device {
  return useSyncExternalStore(watchSize, screenSize);
}

/** The play page for a build, with what its splash launched it with. */
export function playPath(slug: string, build: number, params: LaunchParams = {}): string {
  const query = new URLSearchParams({ build: String(build) });
  if (Object.keys(params).length) query.set('launch', JSON.stringify(params));
  return `/test/${slug}/play?${query}`;
}

/** Reads `launch` from a play page's address; anything malformed is ignored. */
export function launchFromQuery(value: string | null): LaunchParams {
  if (!value) return {};
  try {
    const parsed = launchParamsSchema.safeParse(JSON.parse(value));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}

/**
 * Launching a game from its splash or a Play button: full screen on a phone or tablet, the
 * play page on a computer. Render `layer` somewhere on the page.
 */
export function useLauncher(game: {
  slug: string;
  name: string;
  projectId: string;
  isDeveloper: boolean;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState<{ build: PlayableBuild; params: LaunchParams } | null>(null);
  const { slug, name, projectId, isDeveloper } = game;

  const launch = useCallback(
    (build: PlayableBuild, params: LaunchParams = {}) => {
      if (launchesFullScreen()) setOpen({ build, params });
      else navigate(playPath(slug, build.number, params));
    },
    [navigate, slug],
  );

  const layer = open ? (
    <PhonePlayer
      url={open.build.playUrl}
      name={name}
      projectId={projectId}
      orientation={open.build.orientation}
      launch={open.params}
      isDeveloper={isDeveloper}
      onExit={() => setOpen(null)}
    />
  ) : null;

  return { launch, layer };
}
